/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';

type Theme = 'dark' | 'light' | 'system';

type ThemeProviderProps = {
    children: React.ReactNode;
    defaultTheme?: Theme;
    storageKey?: string;
};

type ThemeProviderState = {
    theme: Theme;
    setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
    theme: 'system',
    setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
    children,
    defaultTheme = 'light',
    storageKey = 'vite-ui-theme',
    ...props
}: ThemeProviderProps) {
    const [theme, setThemeState] = useState<Theme>(
        () => {
            if (typeof window === 'undefined') return defaultTheme;
            // Единственный источник правды — vite-ui-theme в localStorage.
            // Синхронизация из профиля БД происходит через SettingsSync (один раз при входе).
            const storedTheme = localStorage.getItem(storageKey) as Theme;
            if (storedTheme === 'dark' || storedTheme === 'light' || storedTheme === 'system') {
                return storedTheme;
            }
            return defaultTheme;
        }
    );

    // Вспомогательная функция для синхронного обновления DOM
    const applyThemeToDOM = (targetTheme: Theme) => {
        if (typeof window === 'undefined') return;
        
        const root = window.document.documentElement;
        const effectiveTheme = targetTheme === 'system' 
            ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
            : targetTheme;

        // Если тема уже наложена, выходим, чтобы не дергать классы
        if (root.classList.contains(effectiveTheme)) return;

        // Отключаем анимации на время смены переменных CSS
        root.classList.add('disable-transitions');
        
        // Атомарная смена классов
        root.classList.remove('light', 'dark');
        root.classList.add(effectiveTheme);

        // Увеличенная задержка (50мс) гарантирует, что браузер пересчитал стили
        // до того, как анимации снова включатся. Это убирает микро-мерцание.
        const timeout = setTimeout(() => {
            root.classList.remove('disable-transitions');
        }, 50);

        return () => clearTimeout(timeout);
    };

    // Применяем тему при монтировании и изменении стейта
    useEffect(() => {
        const cleanup = applyThemeToDOM(theme);
        return cleanup;
    }, [theme]);

    const value = useMemo(() => ({
        theme,
        setTheme: (newTheme: Theme) => {
            localStorage.setItem(storageKey, newTheme);
            // Сначала обновляем DOM СИНХРОННО, чтобы пропустить один кадр рендеринга React
            applyThemeToDOM(newTheme);
            setThemeState(newTheme);
        },
    }), [theme, storageKey]);

    return (
        <ThemeProviderContext.Provider {...props} value={value}>
            {children}
        </ThemeProviderContext.Provider>
    );
}

export const useTheme = () => {
    const context = useContext(ThemeProviderContext);
    if (context === undefined)
        throw new Error('useTheme must be used within a ThemeProvider');
    return context;
};
